import { createAsyncThunk, createSlice, AsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import eventCategoryService from '../../API/eventCategory';
import { RootState } from '../store';
import SvgMessage from '../../../components/icon/material-icons/Message';
import eventService from '../../API/eventsApi';
import { add } from 'date-fns';

interface Wallet {
	id: string;
	eventId: string;
	currencyId: number;
	balance: number;
	accountNumber: number;
	createdAt: string;
	updatedAt: string;
}

interface Organizer {
	fullname: string;
	id: string;
	firstname: string;
	middlename?: string;
	lastname: string;
	email: string;
	password: string;
	actualPassword?: string;
	status: string;
	location: string;
	mobileNumber: string;
	verified: boolean;
	verificationToken?: string | null;
	passwordResetToken?: string | null;
	passwordConfirmationToken?: string | null;
	passwordResetExpires?: string | null;
	createdAt: string;
	updatedAt: string;
}

interface Event {
	id: string;
	businessMerchantId: string;
	businessId: string;
	eventCategoryId: string;
	title: string;
	description?: string | null;
	startDate: string;
	endDate?: string | null;
	venue: string;
	address: string;
	capacity?: number | null;
	status: string;
	availableTickets?: number | null;
	image: string;
	eventFormat?: string | null;
	time?: string | null;
	rating: number;
	createdAt: string;
	updatedAt: string;
	tickets: any[];
	wallet: Wallet;
	organizer: Organizer;
	base64Image?: string;
}

interface AsyncThunkConfig {
	state: RootState;
	rejectValue: string;
}

export const asyncRetrieveEvents = createAsyncThunk<Event[], string | undefined, AsyncThunkConfig>(
	'event/retrieve',
	async (locationParams, thunkApi) => {
		try {
			const res = await eventService.retrieveAllEvents(locationParams);
			console.log('Location params:', locationParams);
			return res.data.data;
		} catch (error: any) {
			const errorMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			return thunkApi.rejectWithValue(errorMessage);
		}
	});

export const asyncRetrieveEventsForAdmin = createAsyncThunk(
	'event/retrieveForAdmin',
	async (params: Record<string, any>, thunkApi) => {
		try {
			const res = await eventService.retrieveAllEventsForAdmin(params);
			return res.data;
		} catch (error: any) {
			const errorMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			return thunkApi.rejectWithValue(errorMessage);
		}
	},
);

export const asyncRetrieveSingleEvent = createAsyncThunk(
	'event/retrieveSingle',
	async (id: Record<string, any>, thunkApi) => {
		try {
			// @ts-ignore
			const res = await eventService.retrieveEvent(id);
			return res.data.data;
		} catch (error: any) {
			const errorMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			// toast.error(errorMessage);
			return thunkApi.rejectWithValue(errorMessage);
		}
	},
);

const eventsSlice = createSlice({
	name: 'event',
	initialState: {
		data: [] as Event[],
		singleEvent: {} as Event,
		isLoading: false,
		statuses: {
			all: 0,
			active: 0,
			pending: 0,
			postponed: 0,
			cancelled: 0,
			expired: 0,
			inactive: 0
		},
		approvalStatuses: {
			all: 0,
			pending: 0,
			approved: 0,
			rejected: 0
		},
		pagination: {
			total: 0,
			page: 1,
			limit: 10,
			totalPages: 0
		}
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(asyncRetrieveEvents.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveEvents.fulfilled, (state, action) => {
				state.isLoading = false;
				state.data = action.payload;
			})
			.addCase(asyncRetrieveEvents.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRetrieveEventsForAdmin.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveEventsForAdmin.fulfilled, (state, action) => {
				state.isLoading = false;
				state.data = action.payload.data;
				state.statuses = action.payload.statuses;
				state.pagination = action.payload.pagination;
			})
			.addCase(asyncRetrieveEventsForAdmin.rejected, (state) => {
				state.isLoading = false;
			})
			.addCase(asyncRetrieveSingleEvent.pending, (state, action) => {
				state.isLoading = true;
			})
			.addCase(asyncRetrieveSingleEvent.fulfilled, (state, action) => {
				state.isLoading = false;
				state.singleEvent = action.payload;
			})
			.addCase(asyncRetrieveSingleEvent.rejected, (state, action) => {
				state.isLoading = false;
			});
	},
});

export const allEvents = (state: RootState) => state.events;
export default eventsSlice.reducer;
